import type { AgentAttachment } from '@multiplayer-app/ai-agent-types'
import { nanoid } from 'nanoid'
import { AgentAttachmentType } from '@multiplayer-app/ai-agent-types'

export interface FileUploadConfig {
  presignedUrlEndpoint: string
  getAuthHeaders?: () => Record<string, string>
}

export interface FileUploadProgress {
  fileId: string
  fileName: string
  progress: number // 0-100
  status: 'pending' | 'uploading' | 'completed' | 'error'
  error?: string
}

/**
 * Converts a file to base64 string
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1] || result
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Gets presigned URL from backend
 */
async function getPresignedUrl(
  file: File,
  chatId: string | undefined,
  userId: string | undefined,
  config: FileUploadConfig
): Promise<{ url: string; key: string }> {
  const response = await fetch(config.presignedUrlEndpoint, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(config.getAuthHeaders?.() || {})
    },
    body: JSON.stringify({
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
      chatId,
      userId
    })
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Failed to get presigned URL: ${response.status} ${error}`)
  }

  const data = await response.json()
  return { url: data.url, key: data.key }
}

/**
 * Uploads file directly to S3 using presigned URL
 */
async function uploadToS3(file: File, presignedUrl: string, onProgress?: (progress: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const progress = Math.round((e.loaded / e.total) * 100)
        onProgress(progress)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed: network error'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })

    xhr.open('PUT', presignedUrl)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    xhr.send(file)
  })
}

/**
 * Processes a file and creates an attachment
 * All files are uploaded to S3
 */
export async function processFile(
  file: File,
  config: FileUploadConfig,
  options: {
    chatId?: string
    userId?: string
    onProgress?: (progress: FileUploadProgress) => void
  }
): Promise<AgentAttachment> {
  const fileId = nanoid()
  const progress: FileUploadProgress = {
    fileId,
    fileName: file.name,
    progress: 0,
    status: 'pending'
  }

  try {
    options.onProgress?.({ ...progress, status: 'uploading', progress: 0 })

    // All files: upload to S3
    const { url: presignedUrl, key } = await getPresignedUrl(file, options.chatId, options.userId, config)

    await uploadToS3(file, presignedUrl, (uploadProgress) => {
      options.onProgress?.({
        ...progress,
        status: 'uploading',
        progress: uploadProgress
      })
    })

    options.onProgress?.({ ...progress, status: 'completed', progress: 100 })

    // For S3-uploaded files, don't set url - the server will use metadata.s3Key
    // to generate presigned download URLs when needed. The presigned URL from
    // getPresignedUrl is only valid for upload, not for download.
    return {
      id: fileId,
      type: AgentAttachmentType.File,
      name: file.name,
      mimeType: file.type || undefined,
      size: file.size,
      // url is intentionally omitted for S3 files - server uses metadata.s3Key
      metadata: {
        uploadedAt: new Date().toISOString(),
        s3Key: key,
        processingStatus: 'pending',
        size: file.size,
        lastModified: file.lastModified
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    options.onProgress?.({
      ...progress,
      status: 'error',
      error: errorMessage
    })
    throw error
  }
}

/**
 * Processes multiple files in parallel
 */
export async function processFiles(
  files: File[],
  config: FileUploadConfig,
  options: {
    chatId?: string
    userId?: string
    onProgress?: (progress: FileUploadProgress) => void
  }
): Promise<AgentAttachment[]> {
  const results = await Promise.allSettled(files.map((file) => processFile(file, config, options)))

  const attachments: AgentAttachment[] = []
  const errors: Error[] = []

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      attachments.push(result.value)
    } else {
      errors.push(new Error(`Failed to process ${files[index].name}: ${result.reason}`))
    }
  })

  if (errors.length > 0 && attachments.length === 0) {
    // If all failed, throw the first error
    throw errors[0]
  }

  if (errors.length > 0) {
    console.warn('Some files failed to upload:', errors)
  }

  return attachments
}
